import React, { useState, useEffect } from 'react';
import { useBooking } from '../../context/BookingContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { doctorService } from '../../services/doctorService';
import { Doctor } from '../../types';
import { Skeleton } from '../ui/skeleton';
import { toast } from '../ui/sonner';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Alert, AlertTitle, AlertDescription } from '../ui/alert';

const DoctorSelection: React.FC = () => {
  const { selectDoctor } = useBooking();
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageErrors, setImageErrors] = useState<Record<number, boolean>>({});

  // Fetch doctors from API
  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await doctorService.getAll();
        console.log('Fetched doctors:', data);
        setDoctors(data);
      } catch (err) {
        console.error('Error fetching doctors:', err);
        setError('Failed to load doctors. Please try again later.');
        toast.error('Could not load doctors data', {
          description: 'Please check your internet connection or try again later'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDoctors();
  }, []);

  const handleImageError = (doctorId: number) => {
    setImageErrors(prev => ({ ...prev, [doctorId]: true }));
  };

  const handleRetry = () => {
    window.location.reload();
  };

  const handleUseSampleData = () => {
    // Use mock data as fallback
    import('../../data/mockData').then(({ doctors }) => {
      setDoctors(doctors);
      setError(null);
      toast.success('Loaded sample doctors data');
    });
  };

  if (loading) {
    
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-foreground">Choose Your Doctor</h2>
          <p className="text-muted-foreground">Loading available doctors...</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="overflow-hidden">
              <div className="aspect-video w-full">
                <Skeleton className="h-full w-full" />
              </div>
              <CardHeader className="pb-2">
                <Skeleton className="h-6 w-3/4 mb-2" />
                <Skeleton className="h-4 w-1/2" />
              </CardHeader>
              <CardContent className="space-y-4">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-10 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="animate-fade-in">
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error Loading Doctors</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        
        <div className="flex justify-center mt-4 space-x-4">
          <Button 
            variant="outline" 
            onClick={handleRetry}
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Try Again
          </Button>
          
          <Button 
            variant="default"
            onClick={handleUseSampleData}
          >
            Use Sample Data
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-foreground">Choose Your Doctor</h2>
        <p className="text-muted-foreground">Select a healthcare professional that fits your needs</p>
      </div>

      {doctors.length === 0 ? (
        <div className="text-center p-6 bg-gray-50 rounded-lg">
          <p className="text-muted-foreground">No doctors available at the moment.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {doctors.map((doctor) => (
            <Card key={doctor.id} className="overflow-hidden hover:shadow-md transition-shadow">
              <div className="aspect-video w-full overflow-hidden bg-gray-100">
                {doctor.image ? (
                  <img 
                    src={doctor.image} 
                    alt={doctor.name}
                    className="w-full h-full object-cover transition-transform hover:scale-105"
                    onError={() => handleImageError(doctor.id)}
                    style={{ display: imageErrors[doctor.id] ? 'none' : 'block' }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-teal-50 text-teal-500">
                    <span className="text-4xl font-bold">{doctor.name.charAt(0)}</span>
                  </div>
                )}
                {imageErrors[doctor.id] && (
                  <div className="w-full h-full flex items-center justify-center bg-teal-50 text-teal-500">
                    <span className="text-4xl font-bold">{doctor.name.charAt(0)}</span>
                  </div>
                )}
              </div>
              <CardHeader className="pb-2">
                <CardTitle className="text-teal-800">{doctor.name}</CardTitle>
                <CardDescription className="text-teal-600">{doctor.specialty}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground line-clamp-3">{doctor.bio}</p>
                <Button 
                  className="w-full bg-teal-500 hover:bg-teal-600" 
                  onClick={() => selectDoctor(doctor)}
                >
                  Select & Continue
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default DoctorSelection;
